import 'ag-grid-enterprise';

import {
    AG_GRID_LOCALE_EN,
    AG_GRID_LOCALE_FR,
} from '@ag-grid-community/locale';
import { Box } from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import { themeMaterial } from 'ag-grid-community';
import type { AgGridReactProps } from 'ag-grid-react';
import { AgGridReact } from 'ag-grid-react';
import { IS_E2E } from 'env';
import i18n from 'i18n';
import type { ReactNode } from 'react';
import { useMemo } from 'react';

import {
    EXCEL_EMPTY_CELL_CLASS_RULES,
    EXCEL_EMPTY_CELL_STYLE,
} from './constants';
import { withFormulaSafeCsvExport } from './csvExportHelpers';

const getSideButtonShrinkRules = (): Record<string, { flexShrink: string }> => {
    const rules: Record<string, { flexShrink: string }> = {};
    for (let i = 1; i <= 10; i++) {
        rules[`&:nth-of-type(${i})`] = {
            flexShrink: '1' + '00000'.repeat(i - 1),
        };
    }
    return rules;
};

const StyledGridWrapper = styled(Box)(({ theme }) => ({
    width: '100%',
    height: '100%',

    '& .ag-row-even': {
        borderBottom: `1px solid ${theme.palette.divider}`,
    },

    '.ag-header-cell': {
        '.ag-header-cell-filter-button, .ag-header-cell-menu-button.ag-header-menu-always-show':
            {
                display: 'none',
            },
        '&.ag-header-cell-filtered, &.ag-column-menu-visible, &.ag-header-active':
            {
                '.ag-header-cell-filter-button': {
                    display: 'initial',
                },
            },
        '&:hover, &.ag-header-active, &.ag-column-menu-visible': {
            '.ag-header-cell-menu-button.ag-header-menu-always-show': {
                display: 'initial',
            },
        },
    },

    '& .ag-header-cell-text, & .ag-header-group-text': {
        textTransform: 'uppercase !important',
        color: 'black',
    },

    '& .ag-react-container': {
        border: '2px solid #c8c8c8',
        width: '100%',
        height: '100%',
        overflowY: 'auto',
    },

    '& .ag-column-drop-horizontal': {
        backgroundColor: theme.palette.primary.background,
    },

    '& .ag-column-panel': {
        border: '2px solid #c8c8c8',
    },

    '& .ag-side-button-label': {
        fontSize: '14px',
    },

    '& .ag-icon svg': {
        strokeWidth: 1.5,
    },

    '& .ag-icon-tree-closed svg, & .ag-icon-tree-open svg': {
        strokeWidth: 2.5,
    },

    '& .ag-column-drop-cell': {
        border: '2px solid #c8c8c8',
        height: '36px',
        backgroundColor: '#fff',
        padding: '8px',
    },

    '& .ag-header-group-cell.ag-header-group-cell-with-group > .ag-header-cell-comp-wrapper':
        {
            display: 'flex',
            justifyContent: 'center',
        },

    '& .ag-column-select-header': {
        borderBottom: '2px solid #c8c8c8',
        backgroundColor: 'white',
    },

    '& .ag-column-drop-vertical-cell-text': {
        width: '100px',
        overflowWrap: 'break-word',
        whiteSpace: 'pre-line',
    },

    '& .ag-cell-label-container': {
        padding: '0px',
    },

    '& .ag-side-button': {
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',

        '& .ag-side-button-label': {
            transition: 'opacity 0.2s',
            lineHeight: 1.2,
        },
    },

    '& .ag-side-buttons': {
        display: 'flex',
        flexDirection: 'column',

        '& .ag-side-button': {
            display: 'flex',
            flexDirection: 'column',
            flexGrow: 0,
            flexBasis: 'auto',
            minHeight: '40px',

            ...getSideButtonShrinkRules(),

            '& .ag-side-button-button': {
                display: 'flex',
                flexDirection: 'column',
                flex: '1 1 auto',
                minHeight: 0,
            },

            '& .ag-side-button-icon-wrapper': {
                flexShrink: 0,
            },

            '& .ag-side-button-label': {
                display: 'block',
                minHeight: 0,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                transition: 'opacity 0.2s',
                lineHeight: 1.2,
            },
        },
    },
}));

// AG Grid labels the value chips and the aggregation menu by looking the agg
// func name up in localeText, so a custom agg func needs its own entry
const getLocaleText = () => ({
    ...(i18n.language === 'fr' ? AG_GRID_LOCALE_FR : AG_GRID_LOCALE_EN),
    fractionAggFunc: i18n.t('table.fractionAggFunc'),
});

const AgGrid = ({
    className = '',
    defaultColDef,
    excelStyles,
    getContextMenuItems,
    ...props
}: AgGridReactProps & {
    className?: string;
    children?: ReactNode;
}) => {
    const muiTheme = useTheme();

    const defaultColDefWithExcelEmptyCells = useMemo(
        () => ({
            ...defaultColDef,
            cellClassRules: {
                ...defaultColDef?.cellClassRules,
                ...EXCEL_EMPTY_CELL_CLASS_RULES,
            },
        }),
        [defaultColDef],
    );

    const excelStylesWithEmptyCells = useMemo(
        () => [...(excelStyles ?? []), EXCEL_EMPTY_CELL_STYLE],
        [excelStyles],
    );

    // ag-grid-react lets a defined prop override gridOptions, so the callback a
    // grid passes through gridOptions must be wrapped here instead of dropped
    const gridGetContextMenuItems =
        getContextMenuItems ?? props.gridOptions?.getContextMenuItems;
    const getContextMenuItemsWithFormulaSafeCsvExport = useMemo(
        () => withFormulaSafeCsvExport(gridGetContextMenuItems),
        [gridGetContextMenuItems],
    );

    const gridTheme = useMemo(() => {
        return themeMaterial.withParams({
            accentColor: muiTheme.palette.primary.main,
            checkboxCheckedBackgroundColor: muiTheme.palette.primary.main,
            checkboxCheckedShapeColor: '#FFFFFF',
            selectedRowBackgroundColor: `${muiTheme.palette.primary.main}1A`,

            fontSize: '12px',

            iconColor: muiTheme.palette.primary.main,

            headerFontSize: '12px',
            headerTextColor: 'black',
            headerRowBorder: `1px solid ${muiTheme.palette.divider}`,

            sideButtonBarTopPadding: '0',
            sideButtonVerticalPadding: '8px',

            borderColor: muiTheme.palette.lightGrey,
        });
    }, [muiTheme]);

    return (
        <StyledGridWrapper className={className}>
            <AgGridReact
                theme={gridTheme}
                localeText={getLocaleText()}
                floatingFiltersHeight={56}
                suppressFieldDotNotation
                suppressAggFuncInHeader
                suppressCellFocus
                paginationPageSizeSelector={false}
                animateRows={false}
                {...props}
                defaultColDef={defaultColDefWithExcelEmptyCells}
                excelStyles={excelStylesWithEmptyCells}
                getContextMenuItems={
                    getContextMenuItemsWithFormulaSafeCsvExport
                }
                suppressColumnVirtualisation={IS_E2E}
            />
        </StyledGridWrapper>
    );
};

export default AgGrid;
